import { useMemo, useState } from 'react'
import { useNavigate, useSearch } from '@tanstack/react-router'
import { useQueryClient } from '@tanstack/react-query'
import { getTeamRoster } from '../api/manager-team'
import type { RosterMember, RosterSummary } from '../api/manager-team'
import {
  usePagedList,
  type PagedFetchArgs,
  type PagedFetchResult,
} from '../lib/use-paged-list'
import { PaginationControls } from '../components/lists/PaginationControls'
import {
  TeamStatCards,
  RosterHeader,
  RosterRow,
  RosterSkeleton,
  PersonModal,
} from '../components/manager-team'
import { useSession } from '../auth/auth-context'
import { roleAtLeast, type Role } from '../lib/role'
import { PageHead } from '../components/primitives/PageHead'
import { FadeIn } from '../components/primitives/FadeIn'
import { Banner } from '../components/primitives/Banner'
import { Card } from '../components/primitives/Card'
import { displayName } from '../lib/display-name'

// Per-fetch metadata carried alongside the roster rows (in the cached query
// data). Both fields are computed by the api over the whole team before
// slicing, so they're page-independent.
interface RosterMeta {
  summary: RosterSummary
  memberCount: number
  availableTeams: readonly string[]
  availableDepartments: readonly string[]
}

// ── Page ──────────────────────────────────────────────────────────────────────

export function ManagerTeamPage(): JSX.Element {
  // The open person modal is keyed off the URL (?person=<id>) rather than
  // local state so it survives the round-trip through the leave detail page:
  // opening a recent leave navigates away and closing it does history.back(),
  // which remounts this page — local state would be lost, the URL param isn't.
  const navigate = useNavigate()
  const search = useSearch({ strict: false }) as { person?: string }
  const openPersonId = search.person ?? null

  const openPerson = (member: RosterMember): void => {
    void navigate({ to: '/manager/team', search: { person: member.id } })
  }
  const closePerson = (): void => {
    void navigate({ to: '/manager/team', search: {}, replace: true })
  }

  const { user } = useSession()
  // HOD and above oversee a whole department; managers oversee a team. Drives
  // the headcount stat-card label ("People in the department" vs "the team").
  const isDeptScope = user ? roleAtLeast(user.role as Role, 'hod') : false

  const [filter, setFilter] = useState('')
  // Scope filter: 'all' | 'dept:<name>' | 'team:<name>'.
  const [scope, setScope] = useState('all')
  // "On leave this week" stat-card filter — narrows the roster to people with
  // approved leave overlapping the current week (server-side, so it spans all
  // pages rather than just the loaded one).
  const [weekFilter, setWeekFilter] = useState(false)

  const queryClient = useQueryClient()

  // ── Numbered (offset) pagination ──────────────────────────────────────────
  // The roster's `page=` mode returns the exact `total` (plus the team-wide
  // summary + totals), so `usePagedList` maps to it directly and drives
  // <PaginationControls>. The week filter is part of the query key so toggling
  // it resets to page 1 and refetches.
  // Scope narrows SERVER-side (like the week filter) so pagination totals and
  // the row list agree and no member is ever hidden on an unloaded page. Derive
  // the api params from the dropdown value ('all' | 'dept:<n>' | 'team:<n>').
  const departmentParam = scope.startsWith('dept:') ? scope.slice(5) : undefined
  const teamParam = scope.startsWith('team:') ? scope.slice(5) : undefined

  const pagedFetcher = useMemo(
    () =>
      async ({
        page,
        pageSize,
      }: PagedFetchArgs): Promise<PagedFetchResult<RosterMember, RosterMeta>> => {
        const roster = await getTeamRoster({
          page,
          limit: pageSize,
          onLeaveThisWeek: weekFilter,
          ...(teamParam != null ? { team: teamParam } : {}),
          ...(departmentParam != null ? { department: departmentParam } : {}),
        })
        return {
          rows: roster.members,
          total: roster.total ?? roster.totals.memberCount,
          // Carry the team-wide summary + headcount + scope options INSIDE the
          // cached query data rather than pushing them to component state. The
          // api computes them over the whole team before slicing, so they're
          // identical on every page and stable across scope changes. Riding in
          // the cache means they survive a remount served from cache (Team →
          // Rhythm → Team) — a fetch-side-effect setState would vanish there,
          // since the fetcher never re-runs on a cache hit.
          meta: {
            summary: roster.summary,
            memberCount: roster.totals.memberCount,
            availableTeams: roster.availableTeams,
            availableDepartments: roster.availableDepartments,
          },
        }
      },
    [weekFilter, teamParam, departmentParam],
  )
  const pagedList = usePagedList<RosterMember, RosterMeta>({
    // Scope is part of the key so switching it resets to page 1 and refetches.
    queryKey: ['manager-team-roster', 'paged', weekFilter, scope],
    fetcher: pagedFetcher,
  })
  const summary = pagedList.meta?.summary ?? null
  const memberCount = pagedList.meta?.memberCount ?? null
  const availableTeams = pagedList.meta?.availableTeams ?? []
  const availableDepartments = pagedList.meta?.availableDepartments ?? []

  const retryRoster = (): void => {
    void queryClient.invalidateQueries({ queryKey: ['manager-team-roster'] })
  }

  // Rows to render: the current numbered page.
  const displayMembers: RosterMember[] = [...pagedList.rows]

  // Resolve the URL's ?person=<id> against the current page's rows. With
  // numbered pagination only the current page is in memory, so if the id
  // isn't on this page the modal simply stays closed (acceptable — the id
  // becomes resolvable again once the user pages to where the member lives).
  const openMember: RosterMember | null = openPersonId
    ? displayMembers.find((m) => m.id === openPersonId) ?? null
    : null

  // ── Scope + name filter ───────────────────────────────────────────────────
  // Scope is applied SERVER-side (options come from the whole team via the
  // roster response, so they're complete regardless of the loaded page). The
  // name filter stays a client-side quick-find over the current page.
  const departments = availableDepartments
  const teams = availableTeams
  const showScope = departments.length > 1 || teams.length > 1

  const lowerFilter = filter.toLowerCase()
  const filteredMembers =
    filter.length > 0
      ? displayMembers.filter((m) =>
          displayName(m).toLowerCase().includes(lowerFilter),
        )
      : displayMembers

  // ── Render states ────────────────────────────────────────────────────────────

  if (pagedList.isLoading) {
    return (
      <div className="page" data-screen-label="Manager · Team">
        <PageHead
          eyebrow="My team"
          title="Team"
          subtitle="Leave rhythm and upcoming leave for the people you oversee"
        />
        <RosterSkeleton />
      </div>
    )
  }

  if (pagedList.isError) {
    return (
      <div className="page" data-screen-label="Manager · Team">
        <PageHead
          eyebrow="My team"
          title="Team"
          subtitle="Leave rhythm and upcoming leave for the people you oversee"
        />
        <Banner
          kind="warning"
          title="Couldn't load your team"
          subtitle="Please try again."
          action={
            <button
              type="button"
              onClick={() => retryRoster()}
              style={{
                border: 'none',
                background: 'transparent',
                color: 'var(--steel-blue)',
                fontWeight: 600,
                cursor: 'pointer',
                fontSize: 13,
              }}
            >
              Retry
            </button>
          }
        />
      </div>
    )
  }

  const isEmpty = displayMembers.length === 0

  return (
    <>
      <div className="page" data-screen-label="Manager · Team">
        <PageHead
          eyebrow="My team"
          title="Team"
          subtitle="Leave rhythm and upcoming leave for the people you oversee"
        />

        <div style={{ maxWidth: 880 }}>

        {/* At-a-glance stats (folded in from the retired Department Overview):
            headcount for the viewer's scope, on-leave-this-week (click filters
            the list), next holiday. */}
        {summary ? (
          <FadeIn rise={4}>
            <TeamStatCards
              peopleCount={memberCount ?? displayMembers.length}
              peopleLabel={
                isDeptScope ? 'People in the department' : 'People in the team'
              }
              onLeaveThisWeek={summary.onLeaveThisWeek}
              weekFilterActive={weekFilter}
              onToggleWeekFilter={() => setWeekFilter((v) => !v)}
              nextHoliday={summary.nextHoliday}
            />
          </FadeIn>
        ) : null}

        {/* Filter row, divided from the table so the whole thing reads as one
            unit rather than scattered blocks. */}
        <div
          style={{
            display: 'flex',
            flexWrap: 'wrap',
            alignItems: 'flex-end',
            justifyContent: 'flex-start',
            gap: '12px 24px',
            paddingBottom: 14,
            marginBottom: 14,
            borderBottom: '1px solid var(--border)',
          }}
        >
          {/* Scope (all/department/team) + name filter */}
          {showScope || displayMembers.length > 10 ? (
            <div
              style={{
                display: 'flex',
                flexWrap: 'wrap',
                alignItems: 'flex-end',
                gap: 12,
              }}
            >
              {showScope ? (
                <div>
                  <label
                    htmlFor="team-scope"
                    style={{
                      display: 'block',
                      fontSize: 12,
                      fontWeight: 600,
                      color: 'var(--text-2)',
                      marginBottom: 6,
                    }}
                  >
                    Show
                  </label>
                  <select
                    id="team-scope"
                    value={scope}
                    onChange={(e) => setScope(e.target.value)}
                    style={{
                      minWidth: 200,
                      padding: '8px 12px',
                      fontSize: 13,
                      border: '1px solid var(--border)',
                      borderRadius: 8,
                      background: 'var(--surface)',
                      color: 'var(--text)',
                      outline: 'none',
                      boxSizing: 'border-box',
                    }}
                  >
                    {/* "Everyone" spans the whole team, not just the loaded
                        page — use the pre-slice headcount so it matches the
                        stat card rather than the current page's row count. */}
                    <option value="all">
                      Everyone ({memberCount ?? displayMembers.length})
                    </option>
                    {departments.length > 0 ? (
                      <optgroup label="Department">
                        {departments.map((d) => (
                          <option key={d} value={`dept:${d}`}>
                            {d}
                          </option>
                        ))}
                      </optgroup>
                    ) : null}
                    {teams.length > 0 ? (
                      <optgroup label="Team">
                        {teams.map((t) => (
                          <option key={t} value={`team:${t}`}>
                            {t}
                          </option>
                        ))}
                      </optgroup>
                    ) : null}
                  </select>
                </div>
              ) : null}

              {displayMembers.length > 10 ? (
                <div>
                  <label
                    htmlFor="team-filter"
                    style={{
                      display: 'block',
                      fontSize: 12,
                      fontWeight: 600,
                      color: 'var(--text-2)',
                      marginBottom: 6,
                    }}
                  >
                    Filter by name
                  </label>
                  <input
                    id="team-filter"
                    type="search"
                    value={filter}
                    onChange={(e) => setFilter(e.target.value)}
                    placeholder="Filter this page"
                    style={{
                      width: '100%',
                      maxWidth: 320,
                      padding: '8px 12px',
                      fontSize: 13,
                      border: '1px solid var(--border)',
                      borderRadius: 8,
                      background: 'var(--surface)',
                      color: 'var(--text)',
                      outline: 'none',
                      boxSizing: 'border-box',
                    }}
                  />
                </div>
              ) : null}
            </div>
          ) : null}
        </div>

        {/* The scope / name filter only spans the current page. Warn when more
            pages exist so a sparse result doesn't read as "nobody". */}
        {filter.length > 0 && pagedList.totalPages > 1 ? (
          <p style={{ marginTop: -8, marginBottom: 16, fontSize: 12, color: 'var(--text-3)' }}>
            Filtering this page only — clear the filter to page through the rest.
          </p>
        ) : null}

        {/* Members list */}
        {isEmpty ? (
          <FadeIn>
            <Card padding={32}>
              <div
                style={{
                  textAlign: 'center',
                  color: 'var(--text-3)',
                  fontSize: 14,
                }}
              >
                {weekFilter ? (
                  <>
                    No one is on leave this week.{' '}
                    <button
                      type="button"
                      onClick={() => setWeekFilter(false)}
                      style={{
                        border: 'none',
                        background: 'transparent',
                        color: 'var(--steel-blue)',
                        fontWeight: 600,
                        cursor: 'pointer',
                        fontSize: 14,
                        padding: 0,
                      }}
                    >
                      Show everyone
                    </button>
                  </>
                ) : (
                  'No direct reports found. Your approval assignments decide who appears here.'
                )}
              </div>
            </Card>
          </FadeIn>
        ) : (
          <div role="list" aria-label="Team members" style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
            <RosterHeader />
            {filteredMembers.map((member, index) => (
              <FadeIn key={member.id} delayMs={Math.min(index * 30, 150)}>
                <RosterRow
                  member={member}
                  onOpen={openPerson}
                  isSelf={member.id === user?.id}
                />
              </FadeIn>
            ))}
          </div>
        )}

        {/* Numbered pagination */}
        {!isEmpty ? (
          <div style={{ marginTop: 16 }}>
            <PaginationControls
              page={pagedList.page}
              pageSize={pagedList.pageSize}
              total={pagedList.total}
              totalPages={pagedList.totalPages}
              pageSizeOptions={pagedList.pageSizeOptions}
              rangeStart={pagedList.rangeStart}
              rangeEnd={pagedList.rangeEnd}
              canPrev={pagedList.canPrev}
              canNext={pagedList.canNext}
              onPageChange={pagedList.setPage}
              onPageSizeChange={pagedList.setPageSize}
              isFetching={pagedList.isFetching}
              standalone
            />
          </div>
        ) : null}
        </div>
      </div>

      {/* Person detail — centered modal, lifted to page so it renders outside
          the list flow */}
      <PersonModal
        member={openMember}
        onClose={closePerson}
      />
    </>
  )
}
